import React from "react";
import type { Change } from "../../../types/stationPerformance";
import ChangePercentChip from "./ChangePercentChip";

interface DeltaCellProps {
  change: Change | null | undefined;
  /** Inverts the colouring where a rise is bad: free share, for instance. */
  invert?: boolean;
}

/**
 * A month-over-month change, banded by size so a column can be scanned rather than read.
 *
 * `NO_PRIOR_PERIOD` is rendered as words, never as a number. A station's first month has nothing to
 * fall short of, and showing `+100%` would invent a baseline of zero that never existed.
 *
 * Only a real comparable percentage gets a colour. An uncomparable or undefined change stays plain
 * text: painting it would hand the reader a confident signal about a number that does not exist, which
 * is worse than the missing colour.
 */
const DeltaCell: React.FC<DeltaCellProps> = ({ change, invert = false }) => {
  if (!change || change.comparability === "NO_PRIOR_PERIOD") {
    return (
      <span className="text-color-secondary" title="The previous month had no comparable activity">
        no prior period
      </span>
    );
  }
  if (change.percent === null) {
    // A prior period existed but was zero, so a percentage is unbounded rather than large.
    const abs = change.absolute ?? 0;
    return (
      <span className="text-color-secondary" title="The previous month was zero, so a percentage is undefined">
        {abs > 0 ? "+" : ""}
        {abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </span>
    );
  }

  // Shared with the watchlist so one number is one colour everywhere.
  return <ChangePercentChip percent={change.percent} invert={invert} />;
};

export default DeltaCell;
